import React from 'react';

/**
 * Camera height thumbnails — rendered-style SVGs showing the same
 * sphere subject with a small camera pictogram positioned at the
 * chosen height. A dashed sight line runs from the camera's lens
 * to the sphere so the angle of view reads instantly.
 *
 * Design rules:
 *   - 120×90 viewBox, 4:3 aspect (same as every other illustration set).
 *   - Sphere on the right (cx=78, cy=42, r=18) — slightly compact so
 *     there's room for the camera on the left without crowding.
 *   - Shared soft-diffuse gradient on the sphere (matches
 *     lighting/shadow/DOF sets).
 *   - Camera icon = a rounded rectangle body + a small lens circle +
 *     a viewfinder nub on top, scaled to ~16×10 screen units.
 */

const baseStyle: React.CSSProperties = {
    width: '100%',
    aspectRatio: '4/3',
    display: 'block',
};

const SPHERE_CX = 78;
const SPHERE_CY = 42;
const SPHERE_R = 18;
const HORIZON = 62;
const SIGHT = '#5F584C';

const Floor: React.FC<{ fill: string }> = ({ fill }) => (
    <rect x="0" y={HORIZON} width="120" height={90 - HORIZON} fill={fill} />
);

const Sphere: React.FC<{ gradId: string }> = ({ gradId }) => (
    <>
        <defs>
            <radialGradient id={gradId} cx="42%" cy="30%" r="80%">
                <stop offset="0%" stopColor="#EFEEEB" />
                <stop offset="55%" stopColor="#A8A7A4" />
                <stop offset="100%" stopColor="#4A4945" />
            </radialGradient>
        </defs>
        <ellipse cx={SPHERE_CX} cy={HORIZON + 1} rx={SPHERE_R + 4} ry={3} fill="#1F2022" opacity="0.22" />
        <circle cx={SPHERE_CX} cy={SPHERE_CY} r={SPHERE_R} fill={`url(#${gradId})`} />
    </>
);

/**
 * Small camera pictogram — body rectangle + lens circle + viewfinder
 * nub. Parametric so each card can place + rotate the icon to
 * communicate its tilt (rotate around the camera's lens centre).
 */
const CameraIcon: React.FC<{ cx: number; cy: number; rotate?: number }> = ({
    cx,
    cy,
    rotate = 0,
}) => (
    <g transform={`rotate(${rotate} ${cx} ${cy})`}>
        {/* body */}
        <rect x={cx - 8} y={cy - 4} width={14} height={9} rx={1.2} fill="#FFFFFF" stroke={SIGHT} strokeWidth="1" />
        {/* viewfinder nub on top */}
        <rect x={cx - 5} y={cy - 6} width={4} height={2.2} fill={SIGHT} />
        {/* lens */}
        <circle cx={cx + 3} cy={cy + 0.5} r={3} fill="#FFFFFF" stroke={SIGHT} strokeWidth="1" />
        <circle cx={cx + 3} cy={cy + 0.5} r={1.3} fill={SIGHT} />
    </g>
);

// ─── eye level ───────────────────────────────────────────────────
// Camera at the sphere's center height, level gaze → dashed line
// runs horizontally to the sphere's centre.
export const EyeLevelCamera: React.FC = () => (
    <svg viewBox="0 0 120 90" style={baseStyle}>
        <rect x="0" y="0" width="120" height="90" fill="#F4F4F2" />
        <Floor fill="#E4E2DE" />
        <line
            x1={28}
            y1={SPHERE_CY}
            x2={SPHERE_CX - SPHERE_R + 2}
            y2={SPHERE_CY}
            stroke={SIGHT}
            strokeWidth="1"
            strokeDasharray="2.5 2.5"
            opacity="0.65"
            strokeLinecap="round"
        />
        <CameraIcon cx={18} cy={SPHERE_CY} />
        <Sphere gradId="ch-eye-sphere" />
    </svg>
);

// ─── slightly above ──────────────────────────────────────────────
// Camera a bit above the sphere, rotated down ~20° → dashed line
// angles from upper-left down to the top of the sphere.
export const SlightlyAboveCamera: React.FC = () => (
    <svg viewBox="0 0 120 90" style={baseStyle}>
        <rect x="0" y="0" width="120" height="90" fill="#F4F4F2" />
        <Floor fill="#E4E2DE" />
        <line
            x1={24}
            y1={22}
            x2={SPHERE_CX - SPHERE_R + 4}
            y2={SPHERE_CY - 6}
            stroke={SIGHT}
            strokeWidth="1"
            strokeDasharray="2.5 2.5"
            opacity="0.65"
            strokeLinecap="round"
        />
        <CameraIcon cx={18} cy={20} rotate={20} />
        <Sphere gradId="ch-above-sphere" />
    </svg>
);

// ─── low angle ───────────────────────────────────────────────────
// Camera at floor level, rotated up ~25° → dashed line angles from
// lower-left up to the lower portion of the sphere.
export const LowAngleCamera: React.FC = () => (
    <svg viewBox="0 0 120 90" style={baseStyle}>
        <rect x="0" y="0" width="120" height="90" fill="#F4F4F2" />
        <Floor fill="#E4E2DE" />
        <line
            x1={24}
            y1={74}
            x2={SPHERE_CX - SPHERE_R + 4}
            y2={SPHERE_CY + 8}
            stroke={SIGHT}
            strokeWidth="1"
            strokeDasharray="2.5 2.5"
            opacity="0.65"
            strokeLinecap="round"
        />
        <CameraIcon cx={18} cy={76} rotate={-25} />
        <Sphere gradId="ch-low-sphere" />
    </svg>
);

// ─── bird's eye ──────────────────────────────────────────────────
// Camera directly overhead, gaze straight down. No horizon — the
// floor fills the frame. Subject rendered as a top-down flat circle
// with a soft radial highlight so it still reads as a sphere viewed
// from above (not a flat disc).
export const BirdsEyeCamera: React.FC = () => (
    <svg viewBox="0 0 120 90" style={baseStyle}>
        <defs>
            <radialGradient id="ch-birds-sphere" cx="42%" cy="36%" r="68%">
                <stop offset="0%" stopColor="#EFEEEB" />
                <stop offset="100%" stopColor="#8A8986" />
            </radialGradient>
        </defs>
        <rect x="0" y="0" width="120" height="90" fill="#E8E7E3" />
        <line
            x1={60}
            y1={22}
            x2={60}
            y2={38}
            stroke={SIGHT}
            strokeWidth="1"
            strokeDasharray="2.5 2.5"
            opacity="0.65"
            strokeLinecap="round"
        />
        <CameraIcon cx={60} cy={16} rotate={90} />
        <circle cx={60} cy={55} r={20} fill="url(#ch-birds-sphere)" />
    </svg>
);

export const CAMERA_HEIGHT_ILLUSTRATIONS: Record<string, React.FC> = {
    'eye-level': EyeLevelCamera,
    'slightly-above': SlightlyAboveCamera,
    'low-angle': LowAngleCamera,
    'birds-eye': BirdsEyeCamera,
};
